import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Wallet,
  TrendingUp,
  BarChart3,
  DollarSign,
  CheckCircle,
  XCircle,
  Eye,
  Shield
} from 'lucide-react';
import { formatCurrency, formatDate, formatCompactCurrency, payoutsAPI, holdingsAPI } from '../../services/api';
import investorDashboardAPI from '../../services/investorDashboardAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

const InvestorDashboard = () => {
  const navigate = useNavigate();
  const { getDisplayName, user } = useAuth();

  // Check if current user is a sub-investor (created by a custodian)
  const isSubInvestor = user?.createdBy;

  // --- Data Fetching via react-query ---
  // Assumed endpoints implemented in backend (see investorDashboardAPI). Each query handles missing endpoints gracefully.
  const { data: summaryData, isLoading: loadingSummary, error: summaryError } = useQuery({
    queryKey: ['investor-summary'],
    queryFn: async () => {
      const resp = await investorDashboardAPI.getSummary();
      return resp?.data?.data || resp?.data || {};
    }
  });

  // Fetch holdings from the holdings table (replaces allocations)
  const { data: investorHoldingsData = [], isLoading: loadingHoldings, error: holdingsError } = useQuery({
    queryKey: ['investor-my-holdings-dashboard', user?.id],
    queryFn: async () => {
      const resp = await holdingsAPI.getMyHoldings({ status: 'all' });
      const root = resp?.data || resp;
      if (Array.isArray(root?.data)) return root.data;
      if (Array.isArray(root)) return root;
      return [];
    },
    enabled: !!user?.id,
    staleTime: 60_000
  });

  // Filter and aggregate holdings by bond
  const allHoldings = React.useMemo(() => {
    if (!Array.isArray(investorHoldingsData) || investorHoldingsData.length === 0) return [];

    // Filter out inactive holdings and Primary Subscriber direct holdings
    const activeHoldings = investorHoldingsData.filter(holding => {
      if ((holding.remainingTokenAmount || 0) <= 0 || holding.holdingsEndDate) {
        return false;
      }
      const holderSnapshot = holding.holderSnapshot || {};
      if (holderSnapshot.isPrimarySubscriber === true) {
        return false;
      }
      return true;
    });

    // Group by bondId and aggregate amounts
    const holdingsByBond = new Map();
    
    activeHoldings.forEach(holding => {
      const bondIdRaw = typeof holding?.bondId === 'object' && holding?.bondId !== null
        ? holding.bondId._id || holding.bondId.id || holding.bondId
        : holding?.bondId;
      const bondIdKey = bondIdRaw ? String(bondIdRaw) : null;
      
      if (!bondIdKey) return;

      const bondSnapshot = holding.bondSnapshot || {};
      const tokenAmount = holding.remainingTokenAmount || 0;

      if (holdingsByBond.has(bondIdKey)) {
        const existing = holdingsByBond.get(bondIdKey);
        existing.amount += tokenAmount;
        existing.currentValue += tokenAmount;
      } else {
        holdingsByBond.set(bondIdKey, {
          id: bondIdKey,
          _id: bondIdKey,
          bondId: bondIdKey,
          bondName: bondSnapshot.bondName || 'Unnamed Bond',
          isin: bondSnapshot.isin || 'N/A',
          amount: tokenAmount,
          currentValue: tokenAmount,
          status: 'active',
          createdAt: holding.createdAt || holding.holdingsStartDate,
          updatedAt: holding.updatedAt
        });
      }
    });

    return Array.from(holdingsByBond.values());
  }, [investorHoldingsData]);

  // Sort newest first then take latest 5 for display table only
  const latestHoldingsSlice = React.useMemo(() => {
    const sorted = [...allHoldings].sort((a, b) => {
      const da = new Date(a.createdAt || a.updatedAt || 0).getTime();
      const db = new Date(b.createdAt || b.updatedAt || 0).getTime();
      return db - da;
    });
    return sorted.slice(0, 5);
  }, [allHoldings]);

  // Normalize each holding so the table columns have data
  const holdingsData = React.useMemo(() => {
    return latestHoldingsSlice.map(h => ({
      ...h,
      id: h.id || h._id || h.bondId,
      amount: h.amount ?? 0,
      currentValue: h.currentValue ?? h.amount ?? 0
    }));
  }, [latestHoldingsSlice]);

  // Aggregate portfolio value across ALL holdings (not just top 5) by unique bond
  const portfolioAggregation = React.useMemo(() => {
    const totalValue = allHoldings.reduce((sum, h) => sum + (h.currentValue || h.amount || 0), 0);
    const bondsCount = allHoldings.length;
    return { totalValue, bondsCount };
  }, [allHoldings]);

  // Recent payouts (received) - fetch first 5 from payouts API (page 1 limit 5)
  const { data: recentPayoutsData, isLoading: loadingRecentPayouts, error: recentPayoutsError } = useQuery({
    queryKey: ['investor-recent-payouts'],
    queryFn: async () => {
      try {
        const resp = await payoutsAPI.getPayouts({ page: 1, limit: 5 });
        const data = resp?.data?.data;
        return data?.payouts || [];
      } catch (e) {
        return [];
      }
    }
  });

  const { data: walletStatus, isLoading: loadingWallet } = useQuery({
    queryKey: ['investor-wallet-status'],
    queryFn: () => investorDashboardAPI.getWalletStatus()
  });

  // Normalized arrays (fallback to empty)
  const holdings = Array.isArray(holdingsData) ? holdingsData : [];
  // Recent transactions widget removed; query omitted to reduce load.
  // Upcoming payouts widget removed

  // Derived metrics with safe fallbacks
  // Use the full allocation list (not just the latest 5) for fallback metrics if summaryData missing.
  const totalValue = summaryData?.totalCurrentValue ?? portfolioAggregation.totalValue;
  const activeInvestments = summaryData?.activeHoldings ?? portfolioAggregation.bondsCount;
  const totalReturnPct = summaryData?.totalReturnPct ?? summaryData?.totalReturnPercentage ?? null;
  const totalReturnAmount = summaryData?.totalReturnAmount ?? null;

  // Key metrics data
  const keyMetrics = [
    // {
    //   name: 'Total Portfolio Value',
    //   value: formatCompactCurrency(totalValue || 0),
    //   change: totalValue ? `${portfolioAggregation.bondsCount} bond${portfolioAggregation.bondsCount === 1 ? '' : 's'}` : '—',
    //   color: 'primary',
    //   icon: TrendingUp,
    //   href: '/investor/holdings'
    // },
    // {
    //   name: 'Active Investments',
    //   value: String(activeInvestments || 0),
    //   change: `${portfolioAggregation.bondsCount || 0} bond${portfolioAggregation.bondsCount === 1 ? '' : 's'}`,
    //   color: 'success',
    //   icon: BarChart3,
    //   href: '/investor/holdings'
    // }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', class: 'badge-success' },
      completed: { label: 'Completed', class: 'badge-success' },
      scheduled: { label: 'Scheduled', class: 'badge-info' },
      pending: { label: 'Pending', class: 'badge-warning' },
      failed: { label: 'Failed', class: 'badge-error' }
    };
    const config = statusConfig[status] || { label: status, class: 'badge-secondary' };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  // getTransactionTypeIcon removed with Recent Transactions widget.

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Investor Dashboard</h1>
              <p className="mt-1 text-sm text-gray-600">
                Welcome, {getDisplayName()}! Monitor bond holdings
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics */}
        {/* <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {keyMetrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <div
                key={metric.name}
                className="card hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(metric.href)}
              >
                <div className="card-body">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 p-3 rounded-lg bg-${metric.color}-100`}>
                      <Icon className={`h-6 w-6 text-${metric.color}-600`} />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-secondary-600">
                        {metric.name}
                      </p>
                      <p className="text-2xl font-bold text-secondary-900">
                        {metric.value}
                      </p>
                      <p className="text-xs text-secondary-500">
                        {metric.change}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div> */}

        {/* Quick Actions */}
        <div className="card mb-8">
          <div className="card-header">
            <h2 className="text-lg font-medium">Quick Actions</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <button
                onClick={() => navigate('/investor/holdings')}
                className="w-full flex flex-col items-center justify-center p-4 text-sm font-medium rounded border btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                <BarChart3
                  className="w-6 h-6 mb-2"
                  style={{ color: "var(--color-primary)" }}
                />
                View Holdings
              </button>

              <button
                onClick={() => navigate('/investor/payouts')}
                className="w-full flex flex-col items-center justify-center p-4 text-sm font-medium rounded border btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                <DollarSign
                  className="w-6 h-6 mb-2"
                  style={{ color: "var(--color-primary)" }}
                />
                Coupon Management
              </button>

              <button
                onClick={() => navigate('/investor/roles')}
                className="w-full flex flex-col items-center justify-center p-4 text-sm font-medium rounded border btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                <Shield
                  className="w-6 h-6 mb-2"
                  style={{ color: "var(--color-primary)" }}
                />
                Manage Roles
              </button>
              </div>
            </div>
          </div>

        {/* Current Holdings (single full-width section now that Recent Transactions removed) */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center justify-between w-full">
              <h3>Current Holdings</h3>
              <button
                onClick={() => navigate('/investor/holdings')}
                className="btn btn-outline btn-sm"
              >
                View All
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Bond Name</th>
                    <th className="table-header-cell">Amount</th>
                    <th className="table-header-cell">Current Value</th>
                    <th className="table-header-cell">Status</th>
                    {/* <th className="table-header-cell">Actions</th> */}
                  </tr>
                </thead>
                <tbody>
                  {loadingHoldings && (
                    <tr><td colSpan={5} className="py-6 text-center text-sm text-secondary-500">Loading holdings...</td></tr>
                  )}
                  {holdingsError && !loadingHoldings && (
                    <tr><td colSpan={5} className="py-6 text-center text-sm text-error-600">Failed to load holdings</td></tr>
                  )}
                  {!loadingHoldings && !holdingsError && holdings.length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-sm text-secondary-500">No holdings found</td></tr>
                  )}
                  {holdings.map((holding) => (
                    <tr key={holding.id || holding._id} className="border-b border-light-gray hover:bg-secondary-50 transition-colors">
                      <td className="py-4 px-4">
                        <div>
                          <div className="font-semibold text-dark-gray-900 text-sm leading-5">
                            {holding.bondName || holding.name || '—'}
                          </div>
                          <div className="text-xs text-secondary-500 mt-1">
                            ISIN: {holding.isin || holding.ISIN || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-secondary-600 text-sm">
                        {formatCurrency(holding.amount || holding.quantity || 0)}
                      </td>
                      <td className="py-4 px-4 text-secondary-600 text-sm">
                        {formatCurrency(holding.currentValue || holding.marketValue || 0)}
                      </td>
                      <td className="py-4 px-4">
                        {getStatusBadge(holding.status || holding.state || 'active')}
                      </td>
                      {/* <td className="py-4 px-4 text-start">
                        <button
                          onClick={() => navigate(`/investor/holdings/${holding.id || holding._id}`)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg  hover:bg-secondary-200 text-primary-600 hover:text-secondary-700 transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>


        {/* Recent Payouts (last 5 received) */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center justify-between w-full">
              <h3>Recent Payouts</h3>
              <button
                onClick={() => navigate('/investor/payouts')}
                className="btn btn-outline btn-sm"
              >
                View All
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Bond Name</th>
                    <th className="table-header-cell">Type & Amount</th>
                    <th className="table-header-cell">Payment Date</th>
                    <th className="table-header-cell">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRecentPayouts && (
                    <tr><td colSpan={4} className="py-6 text-center text-sm text-secondary-500">Loading recent payouts...</td></tr>
                  )}
                  {recentPayoutsError && !loadingRecentPayouts && (
                    <tr><td colSpan={4} className="py-6 text-center text-sm text-error-600">Failed to load recent payouts</td></tr>
                  )}
                  {!loadingRecentPayouts && !recentPayoutsError && (recentPayoutsData?.length || 0) === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-sm text-secondary-500">No payouts found</td></tr>
                  )}
                  {(recentPayoutsData || []).map(payout => (
                    <tr key={payout.id || payout._id} className="border-b border-light-gray hover:bg-secondary-50 transition-colors">
                      <td className="py-4 px-4">
                        <div className="font-semibold text-dark-gray-900 text-sm leading-5 mb-1">
                          {payout.bondName || payout.bondId?.bondName || 'Unknown Bond'}
                        </div>
                        <div className="text-xs text-secondary-500">
                          ISIN: {payout.isin || payout.bondId?.isin || 'N/A'}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-secondary-600 text-sm">
                        {formatCurrency(payout.paymentAmount || payout.amount || 0)}
                        <div className="text-xs text-secondary-500 mt-1">
                          {(payout.paymentType === 'coupon_payment' || payout.payoutType === 'coupon') && 'Coupon Payment'}
                          {payout.paymentType === 'redemption' && 'Redemption Payment'}
                          {payout.paymentType === 'profit_distribution' && 'Profit Distribution'}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-secondary-600 text-sm">
                        {formatDate(payout.paymentDate || payout.date || payout.dueDate)}
                      </td>
                      <td className="py-4 px-4">
                        {getStatusBadge(payout.status || 'completed')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Wallet Status - Hidden for sub-investors */}
        {!isSubInvestor && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between w-full">
                <h3>Wallet Status</h3>
                <button
                  onClick={() => navigate('/investor/wallet')}
                  className="btn btn-outline btn-sm"
                >
                  Manage Wallet
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-secondary-50 rounded-lg">
                  <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide mb-1">Wallet Address</div>
                  <div className="text-xs text-dark-gray-700 font-mono break-all">
                    {loadingWallet ? 'Loading...' : walletStatus?.address}
                  </div>
                </div>
                <div className="p-3 bg-secondary-50 rounded-lg">
                  <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide mb-1">Network</div>
                  <div className="text-sm text-dark-gray-700">
                    {loadingWallet ? 'Loading...' : walletStatus?.network}
                  </div>
                </div>
                <div className="p-3 bg-secondary-50 rounded-lg">
                  <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide mb-1">Wallet Type</div>
                  <div className="text-sm text-dark-gray-700">
                    {loadingWallet ? 'Loading...' : walletStatus?.walletType}
                  </div>
                </div>
                <div className="p-3 bg-secondary-50 rounded-lg">
                  <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide mb-1">Risk Profile</div>
                  <div className="text-sm text-dark-gray-700">
                    {loadingWallet ? 'Loading...' : walletStatus?.riskProfile}
                  </div>
                </div>
                <div className="p-3 bg-secondary-50 rounded-lg md:col-span-2">
                  <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide mb-1">Last Activity</div>
                  <div className="text-sm text-dark-gray-700">
                    {loadingWallet ? 'Loading...' : (walletStatus?.lastActivity ? formatDate(walletStatus.lastActivity) : '—')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default InvestorDashboard;